"use client"

import { Button } from "../../../../components/ui/button";
import {
  RefreshCw,
  Download,
  FileX,
  AlertCircle,
  Layers,
  Loader2,
  Info,
  Calendar,
  User,
  Tag,
  FileText,
  CheckSquare,
  Square,
  ExternalLink,
  Users,
  Save,
  Trash2,
  Send,
  Paperclip,
  Upload,
  X,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";

import { Badge } from "../../../../components/ui/badge";
import { Checkbox } from "../../../../components/ui/checkbox";
import { DocumentModal } from "../../../../components/ui/DocumentModal";
import { useState, useEffect } from "react";
import { useToast } from "../../../../components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Input } from "../../../../components/ui/input";

// Import the new API functions
import {
  assignItemsToSuppliers,
  getItemsWithAssignedSuppliers,
  createPurchaseOrdersBySupplier,
  sendEmailToAssignedSuppliers,
  removeItemSupplierAssignment,
  getSupplierAssignmentStats,
  updateItemsInStock,
} from "../../../../../ApiCalls/mantisFlowsManangment";

// Import the file attachment API function
import { createFileAndAttach } from "../../../../../ApiCalls/mantisFlowsFiles";

// Helper function to format column names
const formatColumnName = (key) => {
  const columnNames = {
    name: "Nombre del Item",
    item_sku: "SKU",
    item_category_name: "Categoría",
    quantity_to_order: "Cantidad a Ordenar",
  };

  return (
    columnNames[key] ||
    key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};

// Helper function to convert file to base64
const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result.split(",")[1]; // Remove data:application/...;base64, prefix
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const RequestSection = ({
  data,
  currentUser,
  addLog,
  selectedStep,
  orderItems,
  isLoadingItems,
  isLoadingProducts,
  itemsError,
  onRefresh,
  clientId,
  onItemsUpdated,
  products,
  token, // Add token prop for authentication (optional)
}) => {
  const { toast } = useToast();

  // Estado para manejar los checkboxes de los items
  const [checkedItems, setCheckedItems] = useState(new Set());

  // Estado para el modal de documentos
  const [documentModal, setDocumentModal] = useState({
    isOpen: false,
    documents: [],
    item: null,
  });

  // Agregar nuevo estado para manejar las cantidades en existencia
  const [stockQuantities, setStockQuantities] = useState({});
  const [isSavingStock, setIsSavingStock] = useState(false);
  const [hasStockChanges, setHasStockChanges] = useState(false);

  // State for expanded product panels
  const [expandedProduct, setExpandedProduct] = useState(null);

  // State for file upload functionality
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [selectedItemForUpload, setSelectedItemForUpload] = useState(null);

  // Tabla de items
  const itemsData = Array.isArray(orderItems)
    ? orderItems.filter((orderItem) => {
        // Flatten all product items' ids
        const productItemIds = (products || [])
          .flatMap((product) => product.items || [])
          .map((item) => item.id);
        return !productItemIds.includes(orderItem.id);
      })
    : [];
  const hasItems = itemsData.length > 0;

  // Campos a excluir (ocultar)
  const excludedColumns = [
    "id",
    "order_id",
    "external_code",
    "origin_channel",
    "status",
    "order_created_at",
    "file_url",
    "file_filename",
    "assigned_supplier_id",
    "assigned_at",
    "assigned_by",
    "item_code",
    "description",
    "created_at",
    "updated_at",
    "item_id",
    "initial_purchase_request_id",
  ];

  // También excluir todos los campos de documentos numerados
  const dynamicExcludedColumns = [...excludedColumns];
  if (hasItems && itemsData[0]) {
    Object.keys(itemsData[0]).forEach((key) => {
      if (key.match(/^file_(url|filename)\d+$/)) {
        dynamicExcludedColumns.push(key);
      }
    });
  }

  // Campos específicos a mostrar en el orden requerido
  const fieldsToShow = [
    "name",
    "item_sku",
    "item_category_name",
    "quantity_to_order",
  ];

  // Obtener solo los campos que existen en los datos y están en la lista de campos permitidos
  const columnsToShow =
    hasItems && itemsData[0]
      ? fieldsToShow.filter((field) => itemsData[0].hasOwnProperty(field))
      : [];

  // Función para extraer todos los documentos de un item
  const extractDocumentsFromItem = (item) => {
    const documents = [];
    let index = 1;

    // Primero verificar file_url y file_filename (legacy)
    if (item.file_url && item.file_filename) {
      documents.push({
        fileUrl: item.file_url,
        filename: item.file_filename,
      });
    }

    // Luego verificar file_url1, file_url2, etc.
    while (item[`file_url${index}`] || item[`file_filename${index}`]) {
      if (item[`file_url${index}`]) {
        documents.push({
          fileUrl: item[`file_url${index}`],
          filename: item[`file_filename${index}`] || `Document ${index}`,
        });
      }
      index++;
    }

    return documents;
  };

  // Función para abrir documento en modal
  const handleOpenDocument = (item) => {
    const documents = extractDocumentsFromItem(item);
    if (documents.length > 0) {
      setDocumentModal({
        isOpen: true,
        documents: documents,
        item: item,
      });
    }
  };

  // Función para cerrar el modal
  const handleCloseDocumentModal = () => {
    setDocumentModal({
      isOpen: false,
      documents: [],
      item: null,
    });
  };

  // Función para descargar documento
  const handleDownloadDocument = (fileUrl, filename) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = filename || "document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Función para manejar el cambio de checkbox individual
  const handleItemCheck = (itemIndex, checked) => {
    const newCheckedItems = new Set(checkedItems);
    if (checked) {
      newCheckedItems.add(itemIndex);
    } else {
      newCheckedItems.delete(itemIndex);
    }
    setCheckedItems(newCheckedItems);
  };

  // Función para marcar/desmarcar todos los items
  const handleSelectAll = (checked) => {
    if (checked) {
      setCheckedItems(new Set(itemsData.map((_, index) => index)));
    } else {
      setCheckedItems(new Set());
    }
  };

  // Verificar si todos los items están marcados
  const allItemsChecked = hasItems && checkedItems.size === itemsData.length;
  const someItemsChecked =
    checkedItems.size > 0 && checkedItems.size < itemsData.length;

  // Función para manejar el cambio en la cantidad en existencia
  const handleStockQuantityChange = (itemId, value) => {
    setStockQuantities((prev) => ({
      ...prev,
      [itemId]: value,
    }));
    setHasStockChanges(true);

    // Encontrar el item correspondiente
    const item = itemsData.find((item) => item.id === itemId);
    if (item) {
      const currentStock = parseFloat(value) || 0;
      const quantityToOrder = parseFloat(item.quantity_to_order) || 0;

      // Si el stock es suficiente, marcar como Ready automáticamente
      if (currentStock >= quantityToOrder) {
        const itemIndex = itemsData.findIndex((i) => i.id === itemId);
        if (itemIndex !== -1) {
          handleItemCheck(itemIndex, true);
        }
      } else {
        // Si no es suficiente, desmarcar el item
        const itemIndex = itemsData.findIndex((i) => i.id === itemId);
        if (itemIndex !== -1) {
          handleItemCheck(itemIndex, false);
        }
      }
    }
  };

  // Función para obtener el estado del stock
  const getStockStatus = (item) => {
    const currentStock =
      stockQuantities[item.id] !== undefined
        ? parseFloat(stockQuantities[item.id])
        : parseFloat(item.item_stock || 0);
    const quantityToOrder = parseFloat(item.quantity_to_order || 0);

    if (currentStock >= quantityToOrder) {
      return {
        status: "sufficient",
        message: "Stock Suficiente",
        color: "green",
        icon: "✓",
      };
    } else if (currentStock > 0) {
      const difference = quantityToOrder - currentStock;
      return {
        status: "partial",
        message: `Faltan ${
          difference % 1 === 0 ? difference : difference.toFixed(2)
        } unidades`,
        color: "yellow",
        icon: "⚠",
      };
    } else {
      return {
        status: "insufficient",
        message: "Sin Stock",
        color: "red",
        icon: "✗",
      };
    }
  };

  // Función para guardar los cambios de stock
  const handleSaveStock = async () => {
    try {
      setIsSavingStock(true);

      // Convertir el objeto stockQuantities a el formato requerido por la API
      const itemsToUpdate = Object.entries(stockQuantities).map(
        ([id, items_in_stock]) => ({
          id: parseInt(id),
          items_in_stock: parseFloat(items_in_stock),
        })
      );

      await updateItemsInStock(clientId, itemsToUpdate);

      setHasStockChanges(false);
    } catch (error) {
      console.error("Error al guardar el stock:", error);
    } finally {
      setIsSavingStock(false);
    }
  };

  // File upload handlers
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowFileUpload(true); // Show confirmation modal after file selection
    }
  };

  const handleOpenFileUpload = (item) => {
    if (!token) {
      toast({
        title: "Error",
        description: "Token de autenticación no disponible",
        variant: "destructive",
      });
      return;
    }
    setSelectedItemForUpload(item);
    // Directly trigger file input instead of showing modal
    document.getElementById(`file-upload-${item.id}`).click();
  };

  const handleCloseFileUpload = () => {
    setShowFileUpload(false);
    setSelectedFile(null);
    setSelectedItemForUpload(null);
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !clientId || !selectedItemForUpload?.id) {
      toast({
        title: "Error",
        description:
          "Por favor selecciona un archivo y asegúrate de que el item esté cargado",
        variant: "destructive",
      });
      return;
    }

    if (!token) {
      toast({
        title: "Error",
        description: "Token de autenticación no disponible",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploadingFile(true);

      // Convert file to base64
      const base64 = await convertFileToBase64(selectedFile);

      // Prepare file data
      const fileData = {
        base64: base64,
        filename: selectedFile.name,
        mime_type: selectedFile.type,
        size_in_bytes: selectedFile.size,
        uploaded_by: currentUser?._id || currentUser?.email || "unknown",
      };

      // Call the API to create and attach file
      const result = await createFileAndAttach(
        clientId,
        fileData,
        "initial_purchase_request_items", // entity table for items
        selectedItemForUpload.id, // entity ID
        token
      );

      if (result.success) {
        toast({
          title: "Éxito",
          description: "Archivo adjuntado correctamente al item",
          variant: "default",
        });

        // Reset form
        setSelectedFile(null);
        setShowFileUpload(false);
        setSelectedItemForUpload(null);

        // Refresh the items to show the new attachment
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(result.message || "Error al adjuntar el archivo");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Error",
        description: `Error al adjuntar el archivo: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsUploadingFile(false);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setShowFileUpload(false);
  };

  return (
    <div className="p-6 space-y-6 min-h-[600px]">
      {/* Main Content */}
      <div className="space-y-6">
        {/* Items Table - Full Width with Fixed Height */}
        {isLoadingItems ? (
          <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <Layers className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Items de la Solicitud
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Cargando items...
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center h-64 bg-slate-50 dark:bg-slate-800/50">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-3" />
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Loading items...
                </span>
              </div>
            </div>
          </div>
        ) : (
          hasItems && (
            <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                      <Layers className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Items de la Solicitud
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {hasItems
                          ? `${itemsData.length} items en esta solicitud`
                          : "No se encontraron items"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {hasStockChanges && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSaveStock}
                        disabled={isSavingStock}
                        className="border-orange-200 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400"
                      >
                        {isSavingStock ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Guardar Stock
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRefresh}
                      disabled={isLoadingItems}
                      className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400"
                    >
                      {isLoadingItems ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Cargando...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Recargar Items
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Exportar
                    </Button>
                  </div>
                </div>
              </div>

              {/* Fixed height container with scrollbar */}
              <div className="h-96 overflow-y-auto">
                <div className="p-6">
                  {isLoadingItems ? (
                    <div className="flex items-center justify-center h-64 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700">
                      <div className="text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-3" />
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          Loading items...
                        </span>
                      </div>
                    </div>
                  ) : itemsError ? (
                    <div className="flex flex-col items-center justify-center h-64 p-8 bg-red-50 dark:bg-red-900/10 rounded border border-red-200 dark:border-red-800">
                      <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                      <h3 className="text-lg font-semibold text-red-900 dark:text-red-300 mb-2">
                        Failed to Load Items
                      </h3>
                      <p className="text-sm text-red-600 dark:text-red-400 text-center mb-4 max-w-md">
                        {itemsError}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onRefresh}
                        className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Try Again
                      </Button>
                    </div>
                  ) : !hasItems ? (
                    <div className="flex flex-col items-center justify-center h-64 p-8 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700">
                      <FileX className="h-12 w-12 text-slate-400 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-300 mb-2">
                        No Items Found
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
                        This request doesn't contain any items yet.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
                      <Table className="w-full">
                        <TableHeader className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                          <TableRow className="border-slate-200 dark:border-slate-700">
                            <TableHead className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300 text-sm w-20">
                              Documento
                            </TableHead>
                            <TableHead className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 text-sm w-48">
                              Estado del Stock
                            </TableHead>
                            {columnsToShow.map((key) => {
                              // Definir anchos específicos para cada columna
                              const columnWidths = {
                                name: "w-80", // Más ancho para el nombre
                                item_sku: "w-32", // Ancho medio para SKU
                                item_category_name: "w-40", // Ancho medio para categoría
                                quantity_to_order: "w-48", // Ancho mayor para cantidad + stock
                              };

                              return (
                                <TableHead
                                  key={key}
                                  className={`px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 text-sm ${
                                    columnWidths[key] || ""
                                  }`}
                                >
                                  {formatColumnName(key)}
                                </TableHead>
                              );
                            })}
                            <TableHead className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300 text-sm w-24">
                              Adjuntar
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {itemsData.map((item, rowIndex) => {
                            const documents = extractDocumentsFromItem(item);
                            const hasDocument = documents.length > 0;

                            const stockStatus = getStockStatus(item);

                            return (
                              <TableRow
                                key={rowIndex}
                                className={`border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                                  stockStatus.status === "sufficient"
                                    ? "bg-green-50 dark:bg-green-900/10"
                                    : ""
                                }`}
                              >
                                <TableCell className="px-4 py-3 text-center">
                                  {hasDocument ? (
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                        onClick={() => handleOpenDocument(item)}
                                        title={`Open ${
                                          documents.length
                                        } document${
                                          documents.length > 1 ? "s" : ""
                                        }`}
                                      >
                                        <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                      </Button>
                                      {documents.length > 1 && (
                                        <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-medium">
                                          {documents.length}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div
                                      className="flex justify-center"
                                      title="No document available"
                                    >
                                      <FileText className="h-4 w-4 text-slate-300 dark:text-slate-600" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="px-4 py-3">
                                  <div className="flex flex-col gap-2">
                                    <Badge
                                      className={`text-xs font-medium w-fit ${
                                        stockStatus.status === "sufficient"
                                          ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700"
                                          : stockStatus.status === "partial"
                                          ? "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700"
                                          : "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700"
                                      }`}
                                    >
                                      {stockStatus.icon} {stockStatus.message}
                                    </Badge>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-slate-500 dark:text-slate-400">
                                        Stock:
                                      </span>
                                      <Input
                                        type="number"
                                        min="0"
                                        value={
                                          stockQuantities[item.id] !== undefined
                                            ? stockQuantities[item.id]
                                            : item.current_stock || ""
                                        }
                                        onChange={(e) =>
                                          handleStockQuantityChange(
                                            item.id,
                                            e.target.value
                                          )
                                        }
                                        className={`w-20 h-7 text-sm focus:border-orange-400 focus:ring-orange-400/20 ${
                                          stockStatus.status === "sufficient"
                                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                                            : stockStatus.status === "partial"
                                            ? "border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20"
                                            : "border-red-500 bg-red-50 dark:bg-red-900/20"
                                        }`}
                                        placeholder={item.item_stock || "0"}
                                      />
                                    </div>
                                    {stockStatus.status === "partial" && (
                                      <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded border border-amber-200 dark:border-amber-700">
                                        Necesitas{" "}
                                        {parseInt(item.quantity_to_order || 0) -
                                          parseInt(
                                            stockQuantities[item.id] !==
                                              undefined
                                              ? stockQuantities[item.id]
                                              : item.item_stock || 0
                                          )}{" "}
                                        unidades adicionales
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                {columnsToShow.map((key, cellIndex) => {
                                  const value = item[key];
                                  let formattedValue;

                                  if (key === "quantity_to_order") {
                                    formattedValue = (
                                      <div className="flex items-center gap-2">
                                        <span className="text-slate-700 dark:text-slate-300 font-medium text-lg">
                                          {value || "0"}
                                        </span>
                                        <span className="text-sm text-slate-500 dark:text-slate-400">
                                          unidades
                                        </span>
                                      </div>
                                    );
                                  } else if (key === "name") {
                                    formattedValue = (
                                      <div className="flex flex-col">
                                        <span className="font-medium text-slate-900 dark:text-slate-100">
                                          {value || "Sin nombre"}
                                        </span>
                                        {item.description && (
                                          <span className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                                            {item.description}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  } else if (key === "item_sku") {
                                    formattedValue = (
                                      <div className="flex items-center">
                                        <Badge className="bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 font-mono text-xs px-2 py-1">
                                          {value || "N/A"}
                                        </Badge>
                                      </div>
                                    );
                                  } else if (key === "item_category_name") {
                                    const categoryColors = {
                                      PRODUCCION:
                                        "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300",
                                      PERFILERIA:
                                        "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300",
                                      "ALMACEN Y MANTENIMIENTO":
                                        "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300",
                                      MANTENIMIENTO:
                                        "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300",
                                    };
                                    const colorClass =
                                      categoryColors[value] ||
                                      "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/30 dark:text-gray-300";

                                    formattedValue = (
                                      <Badge
                                        className={`${colorClass} text-xs font-medium`}
                                      >
                                        {value || "Sin categoría"}
                                      </Badge>
                                    );
                                  } else {
                                    formattedValue = value || "-";
                                  }

                                  return (
                                    <TableCell
                                      key={cellIndex}
                                      className="px-4 py-3 text-slate-600 dark:text-slate-300 text-sm"
                                    >
                                      {formattedValue}
                                    </TableCell>
                                  );
                                })}
                                <TableCell className="px-4 py-3 text-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenFileUpload(item)}
                                    disabled={isUploadingFile || !token}
                                    className={`h-8 w-8 p-0 ${
                                      token
                                        ? "hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                        : "opacity-50 cursor-not-allowed"
                                    }`}
                                    title={
                                      token
                                        ? "Adjuntar archivo"
                                        : "Token de autenticación no disponible"
                                    }
                                  >
                                    <Paperclip
                                      className={`h-4 w-4 ${
                                        token
                                          ? "text-blue-600 dark:text-blue-400"
                                          : "text-slate-400 dark:text-slate-600"
                                      }`}
                                    />
                                  </Button>

                                  {/* Hidden file input for this item */}
                                  <input
                                    id={`file-upload-${item.id}`}
                                    type="file"
                                    onChange={handleFileSelect}
                                    accept="*/*"
                                    className="hidden"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        )}

        {/* Productos de la Solicitud Section */}
        {isLoadingProducts ? (
          <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                  <Layers className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Productos de la Solicitud
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Cargando productos...
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center h-64 bg-slate-50 dark:bg-slate-800/50">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-green-500 mx-auto mb-3" />
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Loading products...
                </span>
              </div>
            </div>
          </div>
        ) : (
          products &&
          products.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                      <Layers className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Productos de la Solicitud
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {products.length} productos en esta solicitud
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRefresh}
                    disabled={isLoadingProducts}
                    className="border-green-200 text-green-600 hover:bg-green-50 dark:border-green-700 dark:text-green-400"
                  >
                    {isLoadingProducts ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Cargando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Recargar Productos
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {products.map((product, idx) => (
                  <div key={product.id || idx}>
                    <button
                      className="w-full flex items-center justify-between px-6 py-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      onClick={() =>
                        setExpandedProduct(expandedProduct === idx ? null : idx)
                      }
                    >
                      <span className="font-medium text-slate-800 dark:text-slate-100">
                        {product.quantity_to_order
                          ? `${product.quantity_to_order} - ${product.name}`
                          : product.name}{" "}
                        <span className="text-xs text-slate-500 ml-2">
                          ({(product.items || []).length} items)
                        </span>
                      </span>
                      <span className="ml-2">
                        {expandedProduct === idx ? "▲" : "▼"}
                      </span>
                    </button>
                    {expandedProduct === idx && (
                      <div className="px-6 pb-6">
                        {product.items && product.items.length > 0 ? (
                          <div className="space-y-4">
                            {product.items.map((item, itemIdx) => {
                              const documents = extractDocumentsFromItem(item);
                              const hasDocument = documents.length > 0;
                              const stockStatus = getStockStatus(item);
                              return (
                                <div
                                  key={item.id || itemIdx}
                                  className={`rounded border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 ${
                                    stockStatus.status === "sufficient"
                                      ? "bg-green-50 dark:bg-green-900/10"
                                      : ""
                                  }`}
                                >
                                  {/* Item Name and Info */}
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                      <h4 className="font-medium text-slate-900 dark:text-slate-100">
                                        {item.name || "Sin nombre"}
                                      </h4>
                                      {item.item_sku && (
                                        <Badge className="bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 font-mono text-xs px-2 py-1 mt-1">
                                          {item.item_sku}
                                        </Badge>
                                      )}
                                    </div>
                                    {hasDocument && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                        onClick={() => handleOpenDocument(item)}
                                        title={`Open ${
                                          documents.length
                                        } document${
                                          documents.length > 1 ? "s" : ""
                                        }`}
                                      >
                                        <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                      </Button>
                                    )}
                                  </div>

                                  {/* Stock Status */}
                                  <div className="mb-3">
                                    <Badge
                                      className={`text-xs font-medium w-fit ${
                                        stockStatus.status === "sufficient"
                                          ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700"
                                          : stockStatus.status === "partial"
                                          ? "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700"
                                          : "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700"
                                      }`}
                                    >
                                      {stockStatus.icon} {stockStatus.message}
                                    </Badge>
                                  </div>

                                  {/* Stock Input and Requirements */}
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-slate-500 dark:text-slate-400">
                                        Stock:
                                      </span>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={
                                          stockQuantities[item.id] !== undefined
                                            ? stockQuantities[item.id]
                                            : item.item_stock || ""
                                        }
                                        onChange={(e) =>
                                          handleStockQuantityChange(
                                            item.id,
                                            e.target.value
                                          )
                                        }
                                        className={`w-24 h-7 text-sm focus:border-orange-400 focus:ring-orange-400/20 ${
                                          stockStatus.status === "sufficient"
                                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                                            : stockStatus.status === "partial"
                                            ? "border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20"
                                            : "border-red-500 bg-red-50 dark:bg-red-900/20"
                                        }`}
                                        placeholder={item.item_stock || "0.00"}
                                      />
                                    </div>
                                    <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-700">
                                      <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                        IPR necesita:
                                      </span>
                                      <span className="text-xs text-blue-800 dark:text-blue-200 font-bold">
                                        {(() => {
                                          const qty = parseFloat(
                                            item.quantity_to_order || 0
                                          );
                                          return qty % 1 === 0
                                            ? qty
                                            : qty.toFixed(2);
                                        })()}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Additional Info for Partial Stock */}
                                  {stockStatus.status === "partial" && (
                                    <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded border border-amber-200 dark:border-amber-700">
                                      Necesitas{" "}
                                      {(() => {
                                        const needed =
                                          parseFloat(
                                            item.quantity_to_order || 0
                                          ) -
                                          parseFloat(
                                            stockQuantities[item.id] !==
                                              undefined
                                              ? stockQuantities[item.id]
                                              : item.item_stock || 0
                                          );
                                        return needed % 1 === 0
                                          ? needed
                                          : needed.toFixed(2);
                                      })()}{" "}
                                      unidades adicionales
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {hasStockChanges && (
                              <div className="flex justify-end mt-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleSaveStock}
                                  disabled={isSavingStock}
                                  className="border-orange-200 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400"
                                >
                                  {isSavingStock ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Guardando...
                                    </>
                                  ) : (
                                    <>
                                      <Save className="h-4 w-4 mr-2" />
                                      Guardar Stock
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="py-6 text-center text-slate-500 dark:text-slate-400">
                            Este producto no tiene items asociados
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>

      {/* Document Modal */}
      <DocumentModal
        isOpen={documentModal.isOpen}
        onClose={handleCloseDocumentModal}
        documents={documentModal.documents}
        item={documentModal.item}
        onDownload={handleDownloadDocument}
      />

      {/* File Upload Modal */}
      {showFileUpload && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto file-upload-modal">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  Adjuntar Archivo al Item
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseFileUpload}
                  className="h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Item Info */}
              <div className="mb-6">
                <div className="flex items-center space-x-3 p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">
                      {selectedItemForUpload?.name?.charAt(0)?.toUpperCase() ||
                        "I"}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {selectedItemForUpload?.name || "Item"}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      SKU: {selectedItemForUpload?.item_sku || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* File Preview */}
              {selectedFile && (
                <div className="mb-6">
                  <div className="flex items-center space-x-3 p-4 bg-slate-50 dark:bg-slate-700 rounded-xl file-preview">
                    <Paperclip className="h-8 w-8 text-blue-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeSelectedFile}
                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={handleCloseFileUpload}
                  className="flex-1"
                  disabled={isUploadingFile}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleFileUpload}
                  disabled={!selectedFile || isUploadingFile}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white upload-button"
                >
                  {isUploadingFile ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Subiendo...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Upload className="h-4 w-4" />
                      <span>Adjuntar</span>
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
